import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import { Button, Collapse, Group, useMantineColorScheme, useMantineTheme } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import React, { useEffect, useState } from "react";
import { CVItemCard } from "../../../../../components/common/CVItemCard";
import { CVSectionContainer } from "../../../../../components/common/CVSectionContainer";
import { DeleteConfirmationModal } from "../../../../../components/common/DeleteConfirmationModal";
import { ValidationResult } from "../../../../../components/common/ValidationResult";
import { useEditMode } from "../../../../../context/EditModeContext";
import { ProjectExperience as CvProjectExperience } from "../../../../../types/cv";
import { ValidateableCvSections } from "../../../../../types/cvSections";
import { formatDateRange, normalizeYearInput, toUTCDate } from "../../../../../utils/dateFormat";
import {
  ProjectExperience as IProjectExperience,
  TechnologyWithFavorite,
} from "../models/projectExperience.model";
import { ProjectExperienceService } from "../services/projectExperienceService";
import { ProjectExperienceBasicInfo } from "./ProjectExperienceBasicInfo";
import { ProjectExperienceDetails } from "./ProjectExperienceDetails";
import {
  ProjectExperienceForm,
  ProjectExperienceFormData,
} from "./ProjectExperienceForm";
import { ProjectExperienceInstructions } from "./ProjectExperienceInstructions";

interface ProjectExperienceProps {
  projects: IProjectExperience[];
  onChange: (projects: IProjectExperience[]) => void;
  userId?: number;
  cvId?: number;
}

const convertToCvProjectExperience = (project: IProjectExperience): CvProjectExperience => ({
  id: project.id,
  customerId: project.customerId || 0,
  customerName: project.customerName || "",
  internalProjectId: project.internalProjectId || 0,
  personId: project.personId,
  technologies: project.technologies.map(tech => ({
    ...tech,
    skillLevel: tech.skillLevel || 0,
    yearsOfExperience: tech.yearsOfExperience || 0,
    projects: tech.projects || [],
    workExperiences: tech.workExperiences || [],
  })),
  info: project.info || { name: "", description: "" },
  endDate: project.endDate || "",
  startDate: project.startDate,
  roles: project.roles,
  responsibilities: project.responsibilities,
});

export function ProjectExperience({
  projects,
  onChange,
  userId,
  cvId,
}: ProjectExperienceProps) {
  const { isEditMode } = useEditMode();
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';
  const [isAddFormOpen, setIsAddFormOpen] = useState(false);
  const [editingProjectId, setEditingProjectId] = useState<string | null>(null);
  const [expandedDetails, setExpandedDetails] = useState<
    Record<string, boolean>
  >(() => {
    const initial: Record<string, boolean> = {};
    projects.forEach((project) => {
      initial[project.id] = true;
    });
    return initial;
  });
  const projectExperienceService = new ProjectExperienceService();

  const getProjectExperienceSectionData = (): string => {
    if (!projects || projects.length === 0) {
      return 'No project experience entries found.';
    }

    return projects.map((project, index) => {
      const dateRange = formatDateRange(project.startDate, project.endDate);
      const techNames = project.technologies.map(t => t.technology?.name).filter(Boolean).join(', ');
      const roleNames = project.roles.map(r => r.name).filter(Boolean).join(', ');

      return `
Project ${index + 1}:
- Name: ${project.info?.name || 'Unnamed Project'}
- Period: ${dateRange}
- Customer: ${project.customerName || 'Not specified'}
- Roles: ${roleNames || 'None specified'}
- Responsibilities: ${project.responsibilities || 'No responsibilities listed'}
- Technologies: ${techNames || 'None specified'}
- Description: ${project.info?.description || 'No description provided'}
      `.trim();
    }).join('\n\n');
  };

  const getEmptyFormData = (): ProjectExperienceFormData => ({
    id: `temp_${Date.now()}`,
    customerName: "",
    projectName: "",
    description: "",
    responsibilities: [],
    roles: [],
    technologies: [],
    fromMonth: "",
    fromYear: "",
    toMonth: "",
    toYear: "",
  });

  const [newProject, setNewProject] = useState<ProjectExperienceFormData>(
    getEmptyFormData()
  );
  const [editProject, setEditProject] = useState<ProjectExperienceFormData>(
    getEmptyFormData()
  );
  const [originalProject, setOriginalProject] = useState<IProjectExperience | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] =
    useState<IProjectExperience | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Close and reset all forms when edit mode is disabled
  useEffect(() => {
    if (!isEditMode) {
      setIsAddFormOpen(false);
      setEditingProjectId(null);
      const emptyFormData: ProjectExperienceFormData = {
        id: `temp_${Date.now()}`,
        customerName: "",
        projectName: "",
        description: "",
        responsibilities: [],
        roles: [],
        technologies: [],
        fromMonth: "",
        fromYear: "",
        toMonth: "",
        toYear: "",
      };
      setNewProject(emptyFormData);
      setEditProject(emptyFormData);
      setOriginalProject(null);
      setDeleteModalOpen(false);
    }
  }, [isEditMode]);

  const toggleProjectDetails = (projectId: string) => {
    setExpandedDetails((prev) => ({
      ...prev,
      [projectId]: !prev[projectId],
    }));
  };

  const sortedProjects = [...projects].sort((a, b) => {
    const aStartDate = new Date(a.startDate);
    const bStartDate = new Date(b.startDate);

    if (aStartDate.getTime() !== bStartDate.getTime()) {
      return bStartDate.getTime() - aStartDate.getTime();
    }

    const aEndDate = a.endDate ? new Date(a.endDate) : new Date();
    const bEndDate = b.endDate ? new Date(b.endDate) : new Date();

    return bEndDate.getTime() - aEndDate.getTime();
  });

  const expandAllDetails = () => {
    const allExpanded: Record<string, boolean> = {};
    sortedProjects.forEach((project) => {
      allExpanded[project.id] = true;
    });
    setExpandedDetails(allExpanded);
  };

  const collapseAllDetails = () => {
    const allCollapsed: Record<string, boolean> = {};
    sortedProjects.forEach((project) => {
      allCollapsed[project.id] = false;
    });
    setExpandedDetails(allCollapsed);
  };

  const areAllExpanded = sortedProjects.every(
    (project) => expandedDetails[project.id]
  );
  const areAllCollapsed = sortedProjects.every(
    (project) => !expandedDetails[project.id]
  );

  React.useEffect(() => {
    sortedProjects.forEach((project) => {
      if (!(project.id in expandedDetails)) {
        setExpandedDetails((prev) => ({
          ...prev,
          [project.id]: true,
        }));
      }
    });
  }, [sortedProjects, expandedDetails]);

  const handleAddProject = async (formData: ProjectExperienceFormData) => {
    if (!userId) return;

    try {
      const projectEntry = await projectExperienceService.addProjectExperience(
        formData,
        userId
      );
      onChange([...projects, projectEntry]);
      setNewProject(getEmptyFormData());
      setIsAddFormOpen(false);
    } catch (error) {
      console.error("Error adding project:", error);
    }
  };

  const handleEditProject = (project: IProjectExperience) => {
    const formData = projectExperienceService.projectToFormData(project);
    setEditingProjectId(project.id);
    setEditProject(formData);
    setOriginalProject(project);
  };

  const saveProjectEdit = async (formData: ProjectExperienceFormData) => {
    if (!editingProjectId || !userId || !originalProject) return;

    try {
      const updatedProject = await convertFormDataToProject(
        formData,
        userId,
        originalProject
      );
      const savedProject =
        await projectExperienceService.updateProjectExperience(updatedProject);

      onChange(
        projects.map((project) =>
          project.id === editingProjectId ? savedProject : project
        )
      );

      setEditingProjectId(null);
      setOriginalProject(null);
    } catch (error) {
      console.error("Error updating project:", error);
      notifications.show({
        title: "Error",
        message: "Failed to update project experience. Please try again.",
        color: "red",
      });
    }
  };

  const convertFormDataToProject = async (
    projectFormData: ProjectExperienceFormData,
    userId: number,
    originalProject?: IProjectExperience | null
  ): Promise<IProjectExperience> => {
    const technologies = projectFormData.technologies.map(
      (techWithFav: TechnologyWithFavorite) => {
        if (!techWithFav.id) {
          throw new Error(`Technology ID is required for: ${techWithFav.name}`);
        }

        return {
          id: 0,
          technology: {
            id: techWithFav.id,
            name: techWithFav.name,
            categories: [], // Categories will be populated by backend
          },
          personId: userId,
          favourite: techWithFav.isFavorite,
          skillLevel: 0,
          yearsOfExperience: 0,
          projects: [],
          workExperiences: [],
        };
      }
    );

    const roles = projectFormData.roles.map((roleName) => {
      const originalRole = originalProject?.roles?.find(
        (r) => r.name === roleName
      );
      return {
        id: originalRole?.id,
        name: roleName,
      };
    });

    const responsibilities = projectFormData.responsibilities.join("\n");
    const normalizedFromYear = normalizeYearInput(projectFormData.fromYear);
    const normalizedToYear = projectFormData.toYear
      ? normalizeYearInput(projectFormData.toYear)
      : "";

    return {
      id: projectFormData.id,
      customerId: originalProject?.customerId && originalProject.customerId > 0
        ? originalProject.customerId
        : undefined,
      customerName: projectFormData.customerName,
      internalProjectId: originalProject?.internalProjectId && originalProject.internalProjectId > 0
        ? originalProject.internalProjectId
        : undefined,
      personId: userId,
      technologies,
      info: {
        description: projectFormData.description,
        name: projectFormData.projectName,
      },
      endDate:
        !projectFormData.toMonth || !normalizedToYear
          ? undefined
          : toUTCDate(
            new Date(
              parseInt(normalizedToYear),
              parseInt(projectFormData.toMonth) - 1,
              1
            )
          ).toISOString(),
      startDate: toUTCDate(
        new Date(
          parseInt(normalizedFromYear),
          parseInt(projectFormData.fromMonth) - 1,
          1
        )
      ).toISOString(),
      roles,
      responsibilities,
      internalProject: originalProject?.internalProject,
    };
  };

  const handleRemoveProject = async (id: string) => {
    try {
      await projectExperienceService.deleteProjectExperience(id);

      onChange(projects.filter((project) => project.id !== id));

      notifications.show({
        title: "Success",
        message: "Project experience deleted successfully",
        color: "green",
      });
    } catch (error) {
      console.error("Error deleting project:", error);
      notifications.show({
        title: "Error",
        message: "Failed to delete project experience. Please try again.",
        color: "red",
      });
      throw error;
    }
  };

  const openDeleteConfirmation = (project: IProjectExperience) => {
    setProjectToDelete(project);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (projectToDelete) {
      setIsDeleting(true);
      try {
        await handleRemoveProject(projectToDelete.id);
        setDeleteModalOpen(false);
        setProjectToDelete(null);
      } catch (error) {
        console.error("Failed to delete project experience");
      } finally {
        setIsDeleting(false);
      }
    }
  };

  return (
    <CVSectionContainer
      sectionId="project-experience"
      title="Project Experience"
      buttonText={isEditMode ? "Add Project" : undefined}
      onAddClick={isEditMode ? () => setIsAddFormOpen(true) : undefined}
      instructions={isEditMode ? <ProjectExperienceInstructions /> : undefined}
      faqPath="work-vs-project-experience"
      personId={userId}
      cvId={cvId}
      getSectionData={getProjectExperienceSectionData}
    >
      <ValidationResult section={ValidateableCvSections.ProjectExperience} />

      {sortedProjects.length > 0 && (
        <Group justify="flex-end" mb="md">
          <Button
            variant="subtle"
            size="xs"
            leftSection={
              areAllExpanded ? (
                <ChevronUpIcon width={16} height={16} />
              ) : (
                <ChevronDownIcon width={16} height={16} />
              )
            }
            onClick={areAllExpanded ? collapseAllDetails : expandAllDetails}
            disabled={areAllExpanded && areAllCollapsed}
          >
            {areAllExpanded ? "Collapse All Details" : "Expand All Details"}
          </Button>
        </Group>
      )}

      <Collapse in={isAddFormOpen}>
        <ProjectExperienceForm
          project={newProject}
          onChange={setNewProject}
          onSave={handleAddProject}
          onCancel={() => setIsAddFormOpen(false)}
        />
      </Collapse>

      {sortedProjects.map((project) => (
        <React.Fragment key={project.id}>
          {editingProjectId === project.id ? (
            <ProjectExperienceForm
              project={editProject}
              onChange={setEditProject}
              onSave={saveProjectEdit}
              onCancel={() => {
                setEditingProjectId(null);
                setOriginalProject(null);
              }}
              isEditing={true}
            />
          ) : (
            <CVItemCard
              title={`${project.customerName}: ${project.info?.name || "Unnamed Project"
                }`}
              subtitle={formatDateRange(project.startDate, project.endDate)}
              description={
                <div>
                  <ProjectExperienceBasicInfo project={convertToCvProjectExperience(project)} />

                  <Group justify="flex-start" mt="sm">
                    <Button
                      variant="subtle"
                      size="xs"
                      leftSection={
                        expandedDetails[project.id] ? (
                          <ChevronUpIcon width={14} height={14} />
                        ) : (
                          <ChevronDownIcon width={14} height={14} />
                        )
                      }
                      onClick={() => toggleProjectDetails(project.id)}
                    >
                      {expandedDetails[project.id]
                        ? "Hide Details"
                        : "Show Details"}
                    </Button>
                  </Group>

                  <Collapse in={expandedDetails[project.id]}>
                    <ProjectExperienceDetails project={project} />
                  </Collapse>
                </div>
              }
              included={true}
              onEdit={() => handleEditProject(project)}
              onDelete={() => openDeleteConfirmation(project)}
              onToggleInclude={undefined}
              isEditMode={isEditMode}
            />
          )}
        </React.Fragment>
      ))}

      <DeleteConfirmationModal
        opened={deleteModalOpen}
        onClose={() => {
          if (!isDeleting) {
            setDeleteModalOpen(false);
            setProjectToDelete(null);
          }
        }}
        onConfirm={confirmDelete}
        itemName={`${projectToDelete?.customerName || "Unknown"}: ${projectToDelete?.info?.name || "Unnamed Project"
          }`}
        isLoading={isDeleting}
      />
    </CVSectionContainer>
  );
}
